import Cookies from 'js-cookie';
import mapKeys from 'lodash/mapKeys';
import snakeCase from 'lodash/snakeCase';
import set from 'lodash/set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { History } from 'history';
import { UNPROCESSABLE_ENTITY, CONFLICT } from 'http-status-codes';
/* eslint-disable  import/no-cycle */
import { AppThunk } from 'store/store';
import { RootState } from 'store/rootReducer';
import { SignUpFormProps } from 'features/Auth/SignUpSchema';
import { i18n } from 'helpers/i18n';
import { LicensedProducts, LicensedProductsEnum } from 'roles/licensedProducts';
import { axiosInstance } from '../axiosInstance';

const cookiesInstance: Cookies.CookieAttributes = Cookies.withAttributes({
  path: '/',
  domain: process.env.REACT_APP_DOMAIN_NAME
});

export type AuthState = {
  authError: string;
  authenticated: boolean | null;
  // TODO Refator to be a proper type for clearErrors
  errorFields: any;
  fullName: string;
  licensedProducts: LicensedProducts | null;
  role: string;
  token: string | null;
};

const initialState: AuthState = {
  authenticated: null,
  authError: '',
  errorFields: [],
  licensedProducts: null,
  fullName: '',
  role: '',
  token: ''
};

interface SetAuthPayload {
  fullName: string;
  licensedProducts: LicensedProducts | null;
  role: string;
  token: string;
}

const authSlice = createSlice({
  initialState,
  name: 'auth',
  reducers: {
    changeAuthPage: () => ({ ...initialState, authenticated: false }),
    deleteAuth: state => {
      cookiesInstance.remove('4boxes_token');
      cookiesInstance.remove('4boxes_role');
      cookiesInstance.remove('4boxes_full_name');
      cookiesInstance.remove('4boxes_apps');
      return { ...state, authenticated: false, fullName: '', role: '', token: '' };
    },
    getAuth: state => {
      const token = `${Cookies.get('4boxes_token')}`;
      const role = `${Cookies.get('4boxes_role')}`;
      const fullName = `${Cookies.get('4boxes_full_name')}`;
      const apps = Cookies.get('4boxes_apps');
      const licensedProducts = JSON.parse(apps || '{}');

      if (Cookies.get('4boxes_token')) {
        return { ...state, authenticated: true, fullName, role, token, licensedProducts };
      }
      return { ...state, authenticated: false };
    },
    setAuth: (state, { payload }: PayloadAction<SetAuthPayload>) => {
      const { fullName, role, token, licensedProducts } = payload;
      return { ...state, authenticated: true, authError: '', fullName, role, token, licensedProducts };
    },
    setAuthErr: (state, { payload }: PayloadAction<{ fields?: string[]; msg: string }>) => {
      return { ...state, authError: payload.msg, errorFields: payload.fields || [] };
    }
  }
});

const { actions, reducer } = authSlice;
export const { getAuth, deleteAuth, setAuth, setAuthErr, changeAuthPage } = actions;

export const signIn = (
  body: { email: string; password: string },
  history: History,
  redirect?: string | null
): AppThunk => async dispatch => {
  let response;
  try {
    response = await axiosInstance.post('/users/login', body);
  } catch (err) {
    dispatch(setAuthErr({ msg: i18n.t('errors.wrong email or password') }));
    return;
  }
  const { data } = response;
  const { full_name: fullName, role, token, licensed_products: licensedProducts } = data;
  const lps: LicensedProducts = { [LicensedProductsEnum.Core]: false };
  licensedProducts.map((lp: string) => {
    set(lps, lp, true);
  });

  cookiesInstance.set('4boxes_token', token || '');
  cookiesInstance.set('4boxes_role', role);
  cookiesInstance.set('4boxes_full_name', fullName);
  cookiesInstance.set('4boxes_apps', JSON.stringify(lps));

  if (redirect) {
    const REDIRECT_URL = redirect === 'ledger' ? process.env.REACT_APP_LEDGER_URL : process.env.REACT_APP_WAREHOUSE_URL;
    window.location.replace(`${REDIRECT_URL}`);
    return;
  }

  dispatch(
    setAuth({
      fullName,
      role,
      token,
      licensedProducts: lps
    })
  );
  history.push('/products');
};

export const signup = (body: SignUpFormProps, history: History): AppThunk => async dispatch => {
  try {
    await axiosInstance.post(
      '/users',
      mapKeys(body, (v, k) => snakeCase(k))
    );
  } catch (err) {
    if (!err.response) {
      dispatch(setAuthErr({ msg: i18n.t('errors.something went wrong') }));
      return;
    }
    const { status } = err.response;
    switch (status) {
      case CONFLICT:
        dispatch(setAuthErr({ fields: ['email'], msg: i18n.t('errors.user already exists') }));
        break;
      case UNPROCESSABLE_ENTITY:
        dispatch(setAuthErr({ fields: ['email', 'password'], msg: i18n.t('errors.email or password are not valid') }));
        break;
      default:
        dispatch(setAuthErr({ msg: i18n.t('errors.something went wrong') }));
    }
    return;
  }
  const { email, password } = body;
  dispatch(signIn({ email, password }, history));
};

export const isUserAuthenticated = (state: RootState) => state.auth.authenticated;
export const getRole = (state: RootState) => state.auth.role;
export const getLicensedProducts = (state: RootState) => state.auth.licensedProducts;
export const getAuthErrors = (state: RootState) => state.auth.authError;
export const getErrorFields = (state: RootState) => state.auth.errorFields;

export { reducer as auth };
